import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { MarketingCampaign } from '../../queries/marketingCampaigns';

interface GeneratePromoEmailDialogProps {
  campaign: MarketingCampaign;
  onContinue: (context: string) => void;
  onClose: () => void;
}

// Rebuilds the old admin's openEmailGen prompt-context builder - the
// cross-link into the Marketing tab's AI-draft flow that Phase 9c's port
// dropped. Continuing switches to the Marketing tab with this context
// pre-filled; the actual n8n generation call happens there.
export function GeneratePromoEmailDialog({ campaign, onContinue, onClose }: GeneratePromoEmailDialogProps) {
  const offerName = campaign.offer?.name || 'Exclusive Offer';
  const packageCode = campaign.offer?.package_code || '';
  const isFreeGrant = campaign.offer?.offer_type === 'FREE_GRANT';
  const [context, setContext] = useState(
    [
      `Write a personalised re-engagement email for the "${campaign.name}" campaign.`,
      `The offer is: ${offerName}${packageCode ? ` (${packageCode} package)` : ''}.`,
      isFreeGrant ? 'This is a free access grant — no payment required to redeem.' : 'This is a discounted subscription offer.',
      "Each recipient has a unique promo code. Include it prominently in the email body using the Brevo merge tag {{contact.PROMO_CODE}} — Brevo will substitute each user's unique code at send time.",
      'The call-to-action button must link to https://{{SITE_DOMAIN}}/pricing?code={{contact.PROMO_CODE}} so the code is applied in one click rather than typed out — {{SITE_DOMAIN}} is filled in by the email template.',
      'Open by greeting the recipient with {{contact.FIRSTNAME | default : "there"}} — not every contact has a name on file, and that renders "there" for those who do not.',
      'Keep the tone warm and encouraging — these are lapsed or inactive users we want to win back.',
    ].join(' '),
  );

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Generate promo email — {campaign.name}</Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">Reviewed and edited on the Marketing tab before anything is generated.</p>

          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={8}
            className="mt-3 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
          />

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onContinue(context)}
              disabled={!context.trim()}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              Continue to Marketing tab
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
